import { useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap"
import VehicleLink from "components/Common/VehicleLink"

// Common Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

// Hooks
import { useListParams } from "hooks/useListParams"
import {
  useVehicleStockInventories,
  useDeleteVehicleStockInventory,
} from "hooks/useVehicleStockInventory"
import { getVehicleStockInventoryPdf } from "api/vehicleStockInventoryApi"

// Utils
import { formatReadableDate } from "utils/formatDate"

// Auth + RBAC
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"

// Local Components
import InventoryHeader from "./InventoryHeader"

const InventoryList = ({
  showBreadcrumb = false,
  className = "page-content",
  vehicleId = null,
}) => {
  // -------------------- RBAC --------------------
  const { hasAnyPermission } = useAuth()
  const canAdd = hasAnyPermission([ACCESS.VEHICLE_STOCK_INVENTORY.ADD])
  const canEdit = hasAnyPermission([ACCESS.VEHICLE_STOCK_INVENTORY.EDIT])
  const canDelete = hasAnyPermission([ACCESS.VEHICLE_STOCK_INVENTORY.DELETE])
  const canView = hasAnyPermission([ACCESS.VEHICLE_STOCK_INVENTORY.VIEW])

  const navigate = useNavigate()
  const location = useLocation()

  // -------------------- STATE --------------------
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [pdfLoadingId, setPdfLoadingId] = useState(null)

  // -------------------- LIST PARAMS --------------------
  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({
      page: 1,
      search: "",
    })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return {
      page,
      per_page,
      search,
      ...(vehicleId ? { vehicle_id: vehicleId } : {}),
    }
  }, [params, vehicleId])

  // -------------------- API --------------------
  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useVehicleStockInventories(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteVehicleStockInventory()

  // -------------------- NAV HANDLERS --------------------
  const handleAddNewClick = () => {
    const url = vehicleId
      ? `/vehicle-stock-inventory-add?vehicle_id=${vehicleId}`
      : "/vehicle-stock-inventory-add"
    navigate(url)
  }

  const handleEditClick = item => {
    navigate(`/vehicle-stock-inventory-edit/${item.id}`)
  }

  const handleViewClick = item => {
    if (!item?.id) return
    navigate(`/vehicle-stock-inventory-list/${item.id}/view`, {
      state: { from: location },
    })
  }

  const handlePdfClick = async item => {
    if (!item?.id) return
    try {
      await getVehicleStockInventoryPdf(item.id, loading =>
        setPdfLoadingId(loading ? item.id : null)
      )
    } catch (err) {
      console.error(err)
      toast.error("Failed to generate PDF.")
      setPdfLoadingId(null)
    }
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Inventory deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete inventory.")
    }
  }

  // -------------------- TABLE COLUMNS --------------------
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        size: 60,
        cell: ({ row }) => (
          <div className="text-muted fw-semibold">#{row.original.id}</div>
        ),
      },
      {
        header: "Vehicle",
        accessorKey: "vehicleStock",
        cell: ({ row }) => {
          const v = row.original.vehicle_stock || row.original.vehicleStock
          if (!v) return "-"
          return (
            <>
              <div className="fw-semibold">
                <VehicleLink vehicle={v} name={v.stock_id || "-"} />
              </div>
              <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                {v.full_chassis ? (
                  <VehicleLink vehicle={v} name={v.full_chassis} />
                ) : (
                  ""
                )}
              </div>
            </>
          )
        },
      },
      {
        header: "Sender",
        accessorKey: "sender.name",
        cell: ({ row }) => {
          const o = row.original
          const name = o.is_swapped
            ? o.receiver?.name
            : o.sender?.name
          return name || "-"
        },
      },
      {
        header: "Receiver",
        accessorKey: "receiver.name",
        cell: ({ row }) => {
          const o = row.original
          const name = o.is_swapped
            ? o.sender?.name
            : o.receiver?.name
          return name || "-"
        },
      },
      {
        header: "Items",
        accessorKey: "items",
        cell: ({ row }) => {
          const items = Array.isArray(row.original.items)
            ? row.original.items
            : []
          return <Badge color="info">{items.length}</Badge>
        },
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        cell: ({ row }) => (
          <div
            className="text-muted"
            style={{ maxWidth: 220, whiteSpace: "normal" }}
          >
            {row.original.remarks || "-"}
          </div>
        ),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) => (
          <div className="text-muted" style={{ whiteSpace: "nowrap" }}>
            {row.original.created_at
              ? formatReadableDate(row.original.created_at)
              : "-"}
          </div>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex gap-1">
              {canView && (
                <>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleViewClick(data)}
                    id={`view-${data.id}`}
                  >
                    <i className="mdi mdi-eye-outline" />
                  </Button>
                  <UncontrolledTooltip target={`view-${data.id}`}>
                    View
                  </UncontrolledTooltip>
                </>
              )}
              {canView && (
                <>
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => handlePdfClick(data)}
                    disabled={pdfLoadingId === data.id}
                    id={`pdf-${data.id}`}
                  >
                    {pdfLoadingId === data.id ? (
                      <i className="bx bx-loader bx-spin" />
                    ) : (
                      <i className="mdi mdi-download" />
                    )}
                  </Button>
                  <UncontrolledTooltip target={`pdf-${data.id}`}>
                    {pdfLoadingId === data.id ? "Downloading..." : "PDF"}
                  </UncontrolledTooltip>
                </>
              )}
              {canEdit && (
                <>
                  <Button
                    color="warning"
                    size="sm"
                    onClick={() => handleEditClick(data)}
                    id={`edit-${data.id}`}
                  >
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                  <UncontrolledTooltip target={`edit-${data.id}`}>
                    Edit
                  </UncontrolledTooltip>
                </>
              )}
              {canDelete && (
                <>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(data)}
                    id={`delete-${data.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </Button>
                  <UncontrolledTooltip target={`delete-${data.id}`}>
                    Delete
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          )
        },
      },
    ],
    [canView, canEdit, canDelete, pdfLoadingId]
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  // -------------------- RENDER --------------------
  return (
    <PageWrapper
      showBreadcrumb={showBreadcrumb}
      className={className}
      title="Inventory List"
      breadcrumbItem="Inventory List"
    >
      <InventoryHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
        canAdd={canAdd}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />
    </PageWrapper>
  )
}

export default InventoryList
